/**
 * SimulationTenantProposalCard
 *
 * Rendered when the AoA fires `propose_simulation_tenant` — the one question
 * it has to ask before it can test an agent for you: which tenant should the
 * simulation run under? An agent behaves differently per tenant (hierarchical
 * context, tenant files, tenant bundles, tenant-pinned model versions), so
 * "test this agent" is under-specified until that is settled.
 *
 * Why a card and not a panel: this used to open the simulation side panel in a
 * bespoke "setup" mode, which put one decision in a different place from every
 * other decision in the product. Every other question the AoA asks arrives as
 * a card in the chat; this one now does too.
 *
 * On Run the card does three things, in this order:
 *   1. Creates the eval conversation bound to the chosen tenant, so the panel
 *      has something real to open onto.
 *   2. Promotes that eval to the inspector's active slot — the panel opens in
 *      RUNNING mode with the chosen tenant already showing in its top picker
 *      (the panel reads `sim_tenant_id` off the eval).
 *   3. Resolves the proposal, which fires the ordinary continuation stream and
 *      wakes the meta-agent to start driving.
 *
 * Step 3 is what replaced the synthetic user message the panel used to post
 * ("Start the simulation of X under the tenant Y. Talk to it yourself…").
 * The instruction now travels as a tool-resolution signal — see
 * `_simulation_tenant_signal` in proposal_service.py — so the user's side of
 * the transcript only ever contains things the user actually said.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';

import { useLanguage } from '../../../hooks/useLanguage';
import { aoaApi } from '../services/aoaApi';
import type { AoAProposal, ProposalResolution } from '../types';
import { useInspector } from './InspectorContext';
import { TenantHierarchyPicker, type TenantOption } from './TenantHierarchyPicker';

interface Props {
  proposal: AoAProposal;
  onResolved?: (resolution: ProposalResolution, updated: AoAProposal) => void;
}

type SimTenant = TenantOption & { is_home?: boolean };

export const SimulationTenantProposalCard: React.FC<Props> = ({ proposal, onResolved }) => {
  const { t } = useLanguage();
  const inspector = useInspector();

  const payload = (proposal.proposed_payload ?? {}) as Record<string, unknown>;
  const targetChatbotId =
    String(payload.target_chatbot_id ?? proposal.target_entity_id ?? '');
  const targetAgentName = String(payload.target_agent_name ?? '');
  const defaultTenantId = String(payload.default_tenant_id ?? proposal.new_value ?? '');

  const [tenants, setTenants] = useState<SimTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState<string>(proposal.status);

  const isResolved = localStatus !== 'pending';

  // Fetch the selectable tenants from the same endpoint the simulation panel
  // uses, rather than embedding a snapshot in the proposal payload — a card
  // can sit unanswered for a while, and a stale tenant list is a list that
  // offers a tenant the user has since lost access to.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await aoaApi.getSimulatableTenants(targetChatbotId || undefined);
        if (cancelled) return;
        setTenants(rows);
        // Default to the agent's own tenant — the answer that's right often
        // enough that most users can just press Run.
        const preferred =
          rows.find((r) => r.tenant_id === defaultTenantId)
          || rows.find((r) => r.is_bot_home)
          || rows[0];
        if (preferred) setSelectedTenantId(preferred.tenant_id);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [targetChatbotId, defaultTenantId]);

  const selectedTenantName = useMemo(
    () => tenants.find((x) => x.tenant_id === selectedTenantId)?.tenant_name || '',
    [tenants, selectedTenantId],
  );

  const handleRun = useCallback(async () => {
    if (!targetChatbotId || !selectedTenantId || busy || isResolved) return;
    setBusy(true);
    setError(null);
    try {
      // 1. The eval conversation, bound to the chosen tenant.
      const evalRes = await aoaApi.createEvaluation({
        chatbot_id: targetChatbotId,
        tenant_id: selectedTenantId,
        meta_conversation_id: inspector.metaConversationIdRef.current || undefined,
      });

      // 2. Open the panel on it. The panel resolves the tenant shown in its
      //    top picker from the eval's own sim_tenant_id, so the choice made
      //    here is the choice displayed there — no second source of truth.
      inspector.setActiveSimulation({
        kind: 'simulation',
        eval_conversation_id: evalRes.conversation_id,
        target_chatbot_id: targetChatbotId,
        target_agent_name: evalRes.target_agent_name || targetAgentName || 'agent',
      });
      inspector.setCollapsed(false);

      // 3. Resolve → continuation stream → the meta-agent starts driving.
      //    'edit' rather than 'approve' because the user's pick IS the
      //    payload; approve would replay the default the tool suggested.
      const resolution: ProposalResolution = {
        action: 'edit',
        final_value: selectedTenantId,
        edited_payload: {
          new_value: selectedTenantId,
          tenant_name: selectedTenantName,
          eval_conversation_id: evalRes.conversation_id,
        },
      };
      const updated = await aoaApi.resolveProposal(proposal.proposal_id, resolution);
      setLocalStatus('edited');
      onResolved?.(resolution, (updated as any)?.proposal ?? (updated as any));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || String(e));
      setBusy(false);
    }
  }, [
    targetChatbotId, selectedTenantId, selectedTenantName, targetAgentName,
    busy, isResolved, proposal.proposal_id, onResolved, inspector,
  ]);

  const handleCancel = useCallback(async () => {
    if (busy || isResolved) return;
    setBusy(true);
    try {
      const resolution: ProposalResolution = { action: 'reject', reason: '' };
      const updated = await aoaApi.resolveProposal(proposal.proposal_id, resolution);
      setLocalStatus('rejected');
      onResolved?.(resolution, (updated as any)?.proposal ?? (updated as any));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || String(e));
      setBusy(false);
    }
  }, [busy, isResolved, proposal.proposal_id, onResolved]);

  return (
    <div
      className={`aoa-proposal-card aoa-proposal-card--simtenant${
        isResolved ? ' aoa-pcard--resolved-view' : ''
      }`}
      aria-disabled={isResolved || undefined}
      data-testid="aoa-simulation-tenant-card"
    >
      <div className="aoa-proposal-card__header">
        <span className="aoa-proposal-card__tool-label">
          {t('metaAgent.simulation.cardTitle')}
        </span>
        <span className="aoa-proposal-card__entity-name">{targetAgentName}</span>
        {isResolved && (
          <span
            className={`aoa-proposal-card__badge aoa-proposal-card__badge--${
              localStatus === 'rejected' ? 'rejected' : 'applied'
            }`}
          >
            {localStatus === 'rejected'
              ? t('metaAgent.simulation.cardCancelled')
              : t('metaAgent.simulation.cardRunning', { tenant: selectedTenantName })}
          </span>
        )}
      </div>

      <div className="aoa-proposal-card__section">
        <div className="aoa-proposal-card__section-title">
          {t('metaAgent.simulation.chooseTenant')}
        </div>
        <p className="aoa-simtenant__hint">{t('metaAgent.simulation.cardHint')}</p>
        <TenantHierarchyPicker
          tenants={tenants}
          value={selectedTenantId}
          onChange={setSelectedTenantId}
          disabled={busy || isResolved || loading}
          placeholder={loading ? t('common.loading') : t('metaAgent.simulation.selectTenant')}
        />
      </div>

      {error && (
        <Alert
          type="error"
          message={t('metaAgent.simulation.cardRunFailed')}
          description={error}
          showIcon
          className="aoa-proposal-card__error-alert"
        />
      )}

      {!isResolved && (
        <div className="aoa-proposal-card__actions">
          <button
            type="button"
            className="aoa-pcard-btn aoa-pcard-btn-ghost"
            onClick={handleCancel}
            disabled={busy}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="aoa-pcard-btn aoa-pcard-btn-primary"
            onClick={handleRun}
            disabled={busy || loading || !selectedTenantId}
            data-testid="aoa-simulation-tenant-run"
          >
            {busy ? '…' : t('metaAgent.simulation.run')}
          </button>
        </div>
      )}
    </div>
  );
};
